import React, { useState } from 'react';
import { Alert, Button, Card, DatePicker, Form, Input, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import dayjs from 'dayjs';
import { useBatchJob } from '../../hooks/useBatchJob';
import { useBatchTransactionStore } from '../../stores/batch-transaction-store';

const { Dragger } = Upload;

interface Props {
  onNext: () => void;
  onBack: () => void;
}

const ACCEPTED_TYPES = '.csv,.xlsx,.xls';
const MAX_SIZE_MB = 10;

const Step2UploadFile: React.FC<Props> = ({ onNext, onBack }) => {
  const [form] = Form.useForm();
  const { selectedConfig } = useBatchTransactionStore();
  const { createJob } = useBatchJob();
  const [file, setFile] = useState<RcFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const beforeUpload = (f: RcFile): boolean => {
    setError(null);
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext ?? '')) {
      setError('Only CSV and Excel files are supported');
      return false;
    }
    const sizeMb = f.size / 1024 / 1024;
    if (sizeMb > MAX_SIZE_MB) {
      setError(`File must be smaller than ${MAX_SIZE_MB} MB`);
      return false;
    }
    setFile(f);
    return false; // prevent auto-upload
  };

  const handleUpload = async () => {
    if (!file || !selectedConfig?.id) return;
    setUploading(true);
    setError(null);

    try {
      const values = await form.validateFields();
      const txDate = values.transactionDate
        ? (values.transactionDate as dayjs.Dayjs).format('YYYY-MM-DD')
        : undefined;

      const job = await createJob(file, selectedConfig.id, txDate, values.narration);
      if (job) {
        onNext();
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto pt-4 space-y-4">
      <Card title="Upload Batch File">
        <div className="space-y-4">
          <Dragger
            accept={ACCEPTED_TYPES}
            beforeUpload={beforeUpload}
            maxCount={1}
            showUploadList={!!file}
            fileList={file ? [{ uid: '-1', name: file.name, status: 'done', size: file.size }] : []}
            onRemove={() => setFile(null)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag CSV/Excel file here</p>
            <p className="ant-upload-hint">
              Supports .csv, .xlsx, .xls — max {MAX_SIZE_MB} MB
            </p>
          </Dragger>

          <Form form={form} layout="vertical">
            <Form.Item name="transactionDate" label="Override Transaction Date (optional)">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="narration" label="Batch Narration (optional)">
              <Input maxLength={500} placeholder="Description for this batch" />
            </Form.Item>
          </Form>

          {error && <Alert type="error" showIcon message={error} />}

          <div className="flex justify-between">
            <Button onClick={onBack}>Back</Button>
            <Button
              type="primary"
              disabled={!file}
              loading={uploading}
              onClick={handleUpload}
            >
              Upload & Continue
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Step2UploadFile;
